import type { ColumnItem } from "app/pages/sales-intelligence/common-components/export-table/types";
import type { CrmExportResultDto } from "app/pages/sales-intelligence/domain/crm-integration/types/export";
import type { SavedSearchWebhooksServiceType } from "app/pages/sales-intelligence/modules/saved-search-webhooks/services/savedSearchWebhooksService";
import type { SignalsApiWebhookSubscriptionsService } from "app/pages/sales-intelligence/modules/signals-api-webhook/services/signalsApiWebhookSubscriptionsService";
import type { SignalsApiIntegrationServiceApiType } from "app/pages/sales-intelligence/sub-modules/management/store/services/signalsApiIntegrationService";
import type { FetchError } from "app/pages/sales-intelligence/types";

import type { CrmList } from "../types";

import type { CrmListsApiService } from "./services/crmListsService";
import type { DynamicsPredefinedApiServiceType } from "./services/dynamics/predefined";
import type { DynamicsStatusServiceType } from "./services/dynamics/status";
import type { DynamicsTogglesServiceType } from "./services/dynamics/toggles";
import type { DynamicsServiceType } from "./services/dynamicsService";
import type { HubspotPredefinedApiServiceType } from "./services/hubspot/predefined";
import type { HubspotStatusServiceType } from "./services/hubspot/status";
import type { HubspotTogglesServiceType } from "./services/hubspot/toggles";
import type { HubspotServiceType } from "./services/hubspotService";
import type { IntegrationsServiceApiType } from "./services/integrationsService";
import type { SalesforcePredefinedApiServiceType } from "./services/salesforce/predefined";
import type { SalesforceStatusServiceType } from "./services/salesforce/status";
import type { SalesforceToggleServiceType } from "./services/salesforce/toggles";
import type { SalesforceServiceType } from "./services/salesforceService";
import type { SuppressionListServiceType } from "./services/suppressionListService";
import { CONTACT_SOURCE, ExportSettingsSourceType } from "./types/shared";

export type { HubspotPreDefinedType, SalesforcePreDefinedType, PreDefinedResponse } from "./types/shared";
export { ExportSettingsSourceType, CONTACT_SOURCE };

export type ManagementService = HubspotServiceType &
    SalesforceServiceType &
    DynamicsServiceType &
    SuppressionListServiceType &
    SalesforceStatusServiceType &
    HubspotStatusServiceType &
    DynamicsStatusServiceType &
    SalesforceToggleServiceType &
    HubspotTogglesServiceType &
    DynamicsTogglesServiceType &
    HubspotPredefinedApiServiceType &
    SalesforcePredefinedApiServiceType &
    DynamicsPredefinedApiServiceType &
    IntegrationsServiceApiType &
    CrmListsApiService &
    SignalsApiIntegrationServiceApiType &
    SignalsApiWebhookSubscriptionsService &
    SavedSearchWebhooksServiceType;

export enum ExportType {
    "accounts" = 0,
    "contacts" = 2,
    "leads" = 3,
    "companyAsLeads" = 6,
}

export type ExportSettingResponse = {
    overwrite: boolean;
    externalField: string;
    type: ExportType;
    metadata: string;
    sourceType: ExportSettingsSourceType;
    forExport: boolean;
    forEnrichment: boolean;
};

export type ExportSetting = {
    overwrite: boolean;
    externalField: string;
    type?: ExportType;
    metadata?: string;
    sourceType?: ExportSettingsSourceType;
    field: "" | (string & {});
    example: string;
    title: string;
    forExport?: boolean;
    forEnrichment?: boolean;
};

export type ExportItem = ExportSetting & {
    delete?: unknown;
};

export type SaveExportSettingsResponse = {
    value: boolean;
};

export type ExportSettingRequest = {
    externalField: string;
    overwrite: boolean;
    sourceType?: ExportSettingsSourceType;
    metadata?: string;
    forExport?: boolean;
    forEnrichment?: boolean;
};

// Narrow projection of the canonical CRM export result contract (domain level).
export type ExportDomainsResponse = Pick<CrmExportResultDto, "processedRecords" | "exportHistoryUrl">;

export type AddExportSettingsRequest = {
    fields: readonly ExportSettingRequest[];
};

export enum ExportNotification {
    UNSAVED_FIELDS = "UNSAVED_FIELDS",
    DISABLED_EXPORT = "DISABLED_EXPORT_TO_SF",
}

export type SimilarwebFieldConfig = {
    name: string;
    example: string;
    field: string;
    isEnrichmentOnly?: boolean;
    tooltipKey?: string;
};

export type TableSettingsField = {
    similarweb: SimilarwebFieldConfig;
    selected: boolean;
};

export type SimilarwebFieldsConfig = Record<string, SimilarwebFieldConfig>;

export type HubspotExportState = {
    settings: {
        accounts: ExportSetting[];
        contacts: ExportSetting[];
    };
    prevSettings: {
        accounts: ExportSetting[];
        contacts: ExportSetting[];
    };
    columns: HubspotColumns;
    isColumnsLoading: boolean;
    isLoadingSettings: boolean;
    settingDeleteError: FetchError;
    settingsCreateError: FetchError;
    settingsFetchError: FetchError;
    editableField: ExportSetting | undefined;
    deletableField: ExportSetting | undefined;
    isSavingSettings: boolean;
    isSavedSettings: boolean;
    saveSettingsError: FetchError;
    exportingDomainsTo: boolean;
    exportingDomainsToError: FetchError;
    showModalDisabledExport: boolean;
    notification: undefined | ExportNotification;
    exportCompaniesStatus: boolean;
    loadingExportCompaniesStatus: boolean;
    settingExportCompaniesStatus: boolean;
    exportCompaniesStatusError: FetchError;
};

export type DynamicsExportState = {
    settings: DynamicsExportSettings;
    prevSettings: DynamicsExportSettings;
    columns: DynamicsColumns;
    isColumnsLoading: boolean;
    isLoadingSettings: boolean;
    settingDeleteError: FetchError;
    settingsCreateError: FetchError;
    settingsFetchError: FetchError;
    editableField: ExportSetting | undefined;
    deletableField: ExportSetting | undefined;
    isSavingSettings: boolean;
    isSavedSettings: boolean;
    saveSettingsError: FetchError;
    exportingDomainsTo: boolean;
    exportingDomainsToError: FetchError;
    showModalDisabledExport: boolean;
    notification: undefined | ExportNotification;
    exportCompaniesStatus: boolean;
    loadingExportCompaniesStatus: boolean;
    settingExportCompaniesStatus: boolean;
    exportCompaniesStatusError: FetchError;
};

export type CommonManagementStateType = {
    invalidPredefined: {
        accounts: ExportItem[];
        contacts: ExportItem[];
        leads: ExportItem[];
        companyAsLeads: ExportItem[];
    };
    currentExportTab: ExportName;
    previousExportTab: ExportName | "";
    cacheToken: string;
    recordToEnableExport: ExportName | null;
};

export type ExportName = "accounts" | "contacts" | "leads" | "companyAsLeads";
export type HubspotExportName = "accounts" | "contacts";
export type DynamicsExportName = "accounts" | "contacts" | "leads" | "companyAsLeads";

export type NewExportSetting = {
    overwrite?: boolean;
    externalField?: string;
    example?: string;
    field?: string;
    metadata?: string;
    sourceType?: ExportSettingsSourceType;
    forExport?: boolean;
    forEnrichment?: boolean;
};

export type SalesforceExportSettings = Record<ExportName, ExportSetting[]>;
export type HubspotExportSettings = Omit<SalesforceExportSettings, "leads" | "companyAsLeads">;
export type DynamicsExportSettings = Record<DynamicsExportName, ExportSetting[]>;

export type SalesforceColumns = Record<ExportName, ColumnItem[]>;
export type HubspotColumns = Record<HubspotExportName, ColumnItem[]>;
export type DynamicsColumns = Record<DynamicsExportName, ColumnItem[]>;

export type SalesforceExportState = {
    settings: SalesforceExportSettings;
    prevSettings: SalesforceExportSettings;
    columns: SalesforceColumns;
    isColumnsLoading: boolean;
    isLoadingSettings: boolean;

    settingDeleteError: FetchError;
    settingsCreateError: FetchError;
    settingsFetchError: FetchError;
    editableField: ExportSetting | undefined;
    deletableField: ExportSetting | undefined;
    isSavingSettings: boolean;
    isSavedSettings: boolean;
    saveSettingsError: FetchError;
    exportingDomainsToSF: boolean;
    exportingDomainsToSFError: FetchError;
    showModalDisabledExportSF: boolean;
    notification: undefined | ExportNotification;
    settingExportAccountsStatus: boolean;
    exportAccountsStatusError: FetchError;
};

export type ExportSettingWithType = { typeName: ExportName; settings: readonly ExportSetting[] };

export type PredefinedValueType = "Boolean" | "Numeric" | "Picklist" | "Text" | "Date" | "DateTime" | "Reference";

export type CrmListsState = {
    lists: CrmList[];
    isFetching: boolean;
    fetchingError: FetchError;
};
